import { FileUploadBox, useFileUploadState } from "@9amhealth/shared";
import type {
  FileUploadConfig,
  FileUploadControls,
  FileUploadErrorType
} from "@9amhealth/shared";
import { useCallback, useEffect, useRef } from "react";
import FileType from "src/constants/fileType";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import { fileState } from "src/state/state";
import Translate from "../Translate/Translate";

const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const MAX_SIZE_MB = 10;

interface FileUploadLabReportProps {
  onFileIdsChange?: (fileIds: string[]) => void;
  onReadyStateChange?: (ready: boolean) => void;
}

const FileUploadLabReport = ({
  onFileIdsChange,
  onReadyStateChange
}: FileUploadLabReportProps) => {
  const uploadedFileIdsRef = useRef<Map<File, string>>(new Map());

  const formatErrorMessage: FileUploadConfig["formatErrorMessage"] =
    useCallback(
      (error: { type: FileUploadErrorType; file: File }) =>
        error.type === "size"
          ? translate("error.fileSize", { maxSize: MAX_SIZE_MB })
          : translate("defaultError.fileTypeInvalid", { context: "PDF" }),
      []
    );

  const handleFileAccepted = useCallback(
    (file: File, { setStatus, setErrorMessage }: FileUploadControls) => {
      setStatus("uploading");

      fileState
        .uploadFile({
          file,
          fileAttributes: {
            "file.type": FileType.LAB_RESULT,
            "file.tags": ["lab-report"]
          }
        })
        .then((result) => {
          if (!result) {
            throw new Error("Upload failed");
          }

          uploadedFileIdsRef.current.set(file, result.id);
          onFileIdsChange?.(Array.from(uploadedFileIdsRef.current.values()));
          setStatus("uploaded");
        })
        .catch((error) => {
          reportErrorSentry(error);
          setStatus("error");
          setErrorMessage(translate("fileUploadFailed"));
        });
    },
    [onFileIdsChange]
  );

  const { instanceId, files, fileStatuses, fileErrors } = useFileUploadState({
    accept: "application/pdf,.pdf",
    maxSizeBytes: MAX_SIZE_BYTES,
    multiple: true,
    formatErrorMessage,
    onFileAccepted: handleFileAccepted
  });

  useEffect(() => {
    let changed = false;
    for (const [file] of uploadedFileIdsRef.current) {
      if (!files.includes(file)) {
        uploadedFileIdsRef.current.delete(file);
        changed = true;
      }
    }
    if (changed) {
      onFileIdsChange?.(Array.from(uploadedFileIdsRef.current.values()));
    }
  }, [files, onFileIdsChange]);

  useEffect(() => {
    const hasUploading = fileStatuses
      ? Array.from(fileStatuses.values()).includes("uploading")
      : false;
    const hasErrors = fileErrors ? fileErrors.size > 0 : false;
    onReadyStateChange?.(!hasUploading && !hasErrors);
  }, [fileStatuses, fileErrors, onReadyStateChange]);

  return (
    <FileUploadBox
      instanceId={instanceId}
      title={<Translate msg="initialLabs.noRecentLabsFound" />}
      uploadLabel={<Translate msg="fileUpload.uploadFile" />}
      helperText={<Translate msg="fileUpload.pdfOnlyHelper" />}
    >
      <Translate msg="initialLabs.uploadDescription" />
    </FileUploadBox>
  );
};

export default FileUploadLabReport;
